import React, {useMemo, useState} from 'react';
import {
  View,
  TextInput,
  ScrollView,
  ViewStyle,
  ImageStyle,
  TextStyle,
  TouchableOpacity,
} from 'react-native';

import {text} from '../../text';
import {svg} from '../../assets/svg';
import {sizes} from '../../constants';
import {theme} from '../../constants';
import {components} from '../../components';
import {useAppNavigation, useAppSelector} from '../../hooks';
import BottomTabBar from '../../navigation/BottomTabBar';
import {useGetCategoriesQuery} from '../../store/slices/apiSlice';
import {emitLocationPickerEvent} from '../../utils/locationEvents';

const Menu: React.FC = (): JSX.Element => {
  const navigation = useAppNavigation();
  const locationState = useAppSelector((state) => state.locationSlice);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: categoryData,
    error: categoryError,
    isLoading: categoryLoading,
  } = useGetCategoriesQuery();

  const categories = Array.isArray(categoryData) ? categoryData : [];
  const filteredCategories = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return categories;
    }
    return categories.filter((item) =>
      (item.name ?? '').toLowerCase().includes(normalizedQuery),
    );
  }, [categories, searchQuery]);

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const renderHeader = () => {
    return (
      <components.Header
        basket={true}
        user={true}
        userImage={true}
        userName={true}
      />
    );
  };

  const handleLocationRequest = () => {
    emitLocationPickerEvent();
  };

  const renderLocationRequired = () => {
    return (
      <View
        style={{
          paddingHorizontal: 20,
          paddingTop: 40,
          paddingBottom: 40,
          alignItems: 'center',
        }}
      >
        <text.H3 style={{textAlign: 'center', color: theme.colors.textPrimary, marginBottom: 12}}>
          Veuillez entrez une adresse
        </text.H3>
        <text.T16 style={{textAlign: 'center', color: theme.colors.textMuted, marginBottom: 20}}>
          Sélectionnez une adresse valide en appuyant sur le texte en haut de l'écran.
        </text.T16>
        <components.Button
          title='Choisir une adresse'
          onPress={handleLocationRequest}
          containerStyle={{alignSelf: 'stretch'}}
        />
      </View>
    );
  };

  const renderSearchBar = () => {
    return (
      <View
        style={{
          marginTop: 10,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginHorizontal: 20,
          height: 50,
        }}
      >
        <View
          style={{
            backgroundColor: theme.colors.backgroundPrimary,
            flex: 1,
            borderRadius: 10,
            marginRight: 5,
            padding: 5,
            flexDirection: 'row',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <View
            style={{
              backgroundColor: theme.colors.accentPrimary,
              width: 40,
              height: '100%',
              borderRadius: 8,
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: 14,
            }}
          >
            <svg.SearchSvg />
          </View>
          <TextInput
            placeholder='Search ...'
            value={searchQuery}
            onChangeText={setSearchQuery}
            style={{
              flex: 1,
              ...theme.fonts.DMSans_400Regular,
              fontSize: 16,
              color: theme.colors.textPrimary,
            }}
            placeholderTextColor={theme.colors.textMuted}
            autoCorrect={false}
            autoCapitalize='none'
            clearButtonMode='while-editing'
          />
        </View>
        <TouchableOpacity
          style={{
            backgroundColor: theme.colors.backgroundPrimary,
            borderRadius: 10,
            height: 50,
            width: 50,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <svg.FilterSvg />
        </TouchableOpacity>
      </View>
    );
  };

  const renderContent = () => {
    if (categoryLoading) {
      return <components.Loader />;
    }

    if (!locationState.isAddressConfirmed) {
      return (
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingTop: 20,
          }}
          showsVerticalScrollIndicator={false}
        >
          {renderLocationRequired()}
        </ScrollView>
      );
    }

    const cardMaxWidth = 200;
    const blockWidth = Math.min(sizes.width / 2 - 27.7, cardMaxWidth);
    const marginBetweenCards = 15;
    const availableWidth = Math.max(0, sizes.width - 40);
    const itemsPerRow = Math.max(
      1,
      Math.floor((availableWidth + marginBetweenCards) / (blockWidth + marginBetweenCards)),
    );

    const scrollViewStyle: ViewStyle = {
      flexGrow: 1,
      paddingTop: 20,
      paddingHorizontal: 20,
      paddingBottom: 20,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
    };

    const touchableOpacityStyle: ViewStyle = {
      width: blockWidth,
      marginBottom: 15,
      alignItems: 'center',
    };

    const imageSize = blockWidth;

    const imageStyle: ImageStyle = {
      width: imageSize,
      height: imageSize,
      borderRadius: 10,
    };

    const placeholderStyle: ViewStyle = {
      width: imageSize,
      height: imageSize,
      borderRadius: 10,
      backgroundColor: theme.colors.backgroundPrimary,
      justifyContent: 'center',
      alignItems: 'center',
    };

    const textStyle: TextStyle = {
      color: theme.colors.textPrimary,
      textTransform: 'capitalize',
      textAlign: 'center',
      marginTop: 12,
    };

    if (filteredCategories.length === 0) {
      return (
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingTop: 20,
            paddingHorizontal: 20,
            paddingBottom: 20,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          showsVerticalScrollIndicator={false}
        >
          <text.T16 style={{color: theme.colors.textMuted, textAlign: 'center'}}>
            Aucun résultat ne correspond à votre recherche.
          </text.T16>
        </ScrollView>
      );
    }

    return (
      <ScrollView
        contentContainerStyle={{...scrollViewStyle}}
        showsVerticalScrollIndicator={false}
      >
        {filteredCategories.map((item, index) => {
          const isEndOfRow = (index + 1) % itemsPerRow === 0;
          const isLastItem = index === filteredCategories.length - 1;
          const cardMarginRight = isEndOfRow || isLastItem ? 0 : marginBetweenCards;
          return (
            <TouchableOpacity
              key={item.id}
              style={{
                ...touchableOpacityStyle,
                marginRight: cardMarginRight,
              }}
              onPress={() => {
                navigation.navigate('Menulist', {
                  category: item.id,
                });
              }}
            >
              {item.imageUrl ? (
                <components.Image
                  source={{uri: item.imageUrl}}
                  style={imageStyle}
                  resizeMode='cover'
                />
              ) : (
                <View style={placeholderStyle} />
              )}
              <text.T16 numberOfLines={2} style={{...textStyle}}>
                {item.name}
              </text.T16>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    );
  };

  const renderBottomTabBar = () => {
    return <BottomTabBar />;
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderSearchBar()}
      {renderContent()}
      {renderBottomTabBar()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default Menu;
